/**
 * Org-level Customer-Managed Encryption Keys (CMK / BYOK) card.
 *
 * Mounted as a section inside `OrgSecurityPage` so the customer's
 * security controls live in a single place. Backed by:
 *
 *   GET    /api/orgs/:orgId/encryption                  — state + prereqs.
 *   POST   /api/orgs/:orgId/encryption/enroll           — first-time enrolment.
 *   POST   /api/orgs/:orgId/encryption/rotate           — KEK + DEK rotation.
 *   POST   /api/orgs/:orgId/encryption/disable          — start 30-day grace.
 *   POST   /api/orgs/:orgId/encryption/cancel-disable   — abort the grace.
 *   GET    /api/orgs/:orgId/encryption/recovery-sheet/:id
 *
 * Lives in `components/` (not `pages/`) because the panel is part of
 * the existing /dashboard/security page — keeps procurement-relevant
 * security toggles together rather than scattering them across
 * separate routes.
 *
 * UX:
 *   • Pre-enrolment: shows the two prereqs (MFA mandate + SIEM
 *     endpoint) as a checklist, with deep-anchors to the matching
 *     toggles on the same page. Enrol form is disabled until both
 *     prereqs are green and the typed confirmation matches.
 *   • Enrolled: shows the fingerprint, state, last rotation, the
 *     list of in-scope tables, and the Rotate / Disable buttons.
 *   • Disabled (grace): shows a countdown to crypto-shred and a
 *     prominent Cancel-disable button.
 *   • Crypto-shredded: shows a tombstone — re-enrol is allowed but
 *     historical data is gone.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, RefreshCw, ShieldOff, ShieldCheck, AlertTriangle, Download } from 'lucide-react';
import type { OrgCmkPublicState } from '@rynora/contracts';
import { getApiBaseUrl } from '../../../lib/config';

const API_BASE: string = getApiBaseUrl();

interface OrgCmkCardProps {
  orgId: string;
  orgSlug: string;
  canEdit: boolean;
  /** Caller already supplied an auth fetch; we receive only the token here. */
  authToken: string;
}

interface RecoverySheet {
  orgId: string;
  kekFingerprint: string;
  label: string | null;
  inScopeTables: string[];
  enrolledAt: string;
  expiresAt: string;
  disableSemantics: {
    graceDays: number;
    cryptoShreddedIsTerminal: boolean;
    cancelDuringGraceRestoresAccess: boolean;
  };
}

const ENROL_CONFIRM = 'ENROLL';
const ROTATE_CONFIRM = 'ROTATE';

function authFetch(path: string, token: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

export function OrgCmkCard({ orgId, orgSlug, canEdit, authToken }: OrgCmkCardProps): JSX.Element {
  const [state, setState] = useState<OrgCmkPublicState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Enrol form
  const [enrolKek, setEnrolKek] = useState('');
  const [enrolLabel, setEnrolLabel] = useState('');
  const [enrolConfirm, setEnrolConfirm] = useState('');

  // Rotate form
  const [rotateKek, setRotateKek] = useState('');
  const [rotateLabel, setRotateLabel] = useState('');
  const [rotateConfirm, setRotateConfirm] = useState('');

  // Disable form
  const [disableReason, setDisableReason] = useState('');
  const [disableSlug, setDisableSlug] = useState('');

  // Recovery sheet (returned by enrol; lives in memory for 10 minutes)
  const [recoverySheet, setRecoverySheet] = useState<RecoverySheet | null>(null);
  const [recoverySheetExpiresAt, setRecoverySheetExpiresAt] = useState<string | null>(null);

  const fetchState = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`/api/orgs/${orgId}/encryption`, authToken);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErr(body?.message || `Failed to load CMK state (${r.status})`);
        return;
      }
      setState(await r.json());
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [orgId, authToken]);

  useEffect(() => {
    void fetchState();
  }, [fetchState]);

  const prereqsOk = useMemo(
    () => Boolean(state?.prerequisites?.mfaMandateEnabled && state?.prerequisites?.siemEndpointConfigured),
    [state],
  );

  const handleEnrol = async () => {
    if (!canEdit) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await authFetch(`/api/orgs/${orgId}/encryption/enroll`, authToken, {
        method: 'POST',
        body: JSON.stringify({
          kekBase64: enrolKek.trim(),
          label: enrolLabel.trim() || undefined,
          confirmation: ENROL_CONFIRM,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(body?.message || `Enrol failed (${r.status})`);
        return;
      }
      setEnrolKek('');
      setEnrolLabel('');
      setEnrolConfirm('');
      setState(body.state);
      // Pull the recovery sheet straight away.
      if (body.recoverySheetId) {
        const sheetResp = await authFetch(
          `/api/orgs/${orgId}/encryption/recovery-sheet/${body.recoverySheetId}`,
          authToken,
        );
        if (sheetResp.ok) {
          setRecoverySheet(await sheetResp.json());
          setRecoverySheetExpiresAt(body.recoverySheetExpiresAt);
        }
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async () => {
    if (!canEdit) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await authFetch(`/api/orgs/${orgId}/encryption/rotate`, authToken, {
        method: 'POST',
        body: JSON.stringify({
          newKekBase64: rotateKek.trim(),
          newLabel: rotateLabel.trim() || undefined,
          confirmation: ROTATE_CONFIRM,
          reEncryptInScopeRows: true,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(body?.message || `Rotate failed (${r.status})`);
        return;
      }
      setRotateKek('');
      setRotateLabel('');
      setRotateConfirm('');
      setState(body);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!canEdit) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await authFetch(`/api/orgs/${orgId}/encryption/disable`, authToken, {
        method: 'POST',
        body: JSON.stringify({
          reason: disableReason.trim(),
          confirmationOrgSlug: disableSlug.trim(),
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(body?.message || `Disable failed (${r.status})`);
        return;
      }
      setDisableReason('');
      setDisableSlug('');
      setState(body);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleCancelDisable = async () => {
    if (!canEdit) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await authFetch(`/api/orgs/${orgId}/encryption/cancel-disable`, authToken, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(body?.message || `Cancel-disable failed (${r.status})`);
        return;
      }
      setState(body);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const downloadRecoverySheet = () => {
    if (!recoverySheet) return;
    const blob = new Blob([JSON.stringify(recoverySheet, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rynora-cmk-recovery-${recoverySheet.kekFingerprint.slice(0, 12)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) {
    return (
      <section className="sso-settings-section" aria-busy="true">
        <h2>
          <KeyRound size={18} aria-hidden="true" /> Customer-managed encryption keys
        </h2>
        <p className="sso-settings-hint">Loading…</p>
      </section>
    );
  }

  if (!state) {
    return (
      <section className="sso-settings-section">
        <h2>
          <KeyRound size={18} aria-hidden="true" /> Customer-managed encryption keys
        </h2>
        <p className="sso-settings-error">{err ?? 'Failed to load CMK state.'}</p>
      </section>
    );
  }

  const fingerprintShort = state.kekFingerprint ? `${state.kekFingerprint.slice(0, 12)}…` : null;

  return (
    <section className="sso-settings-section" id="cmk">
      <h2>
        <KeyRound size={18} aria-hidden="true" /> Customer-managed encryption keys
      </h2>
      <p className="sso-settings-hint">
        Bring your own AES-256 Key Encryption Key (KEK). Rynora generates a per-org Data Encryption Key (DEK), wraps it
        with your KEK, and uses it to encrypt the in-scope tenant secrets below. You can rotate the KEK any time and
        Disable starts a 30-day grace before crypto-shredding the wrapped DEK (after which the rows are unrecoverable by
        anyone, including Rynora).
      </p>

      {err && (
        <p className="sso-settings-error" style={{ marginTop: 8 }}>
          {err}
        </p>
      )}

      <div
        style={{
          marginTop: 16,
          padding: 12,
          border: '1px solid var(--border, #d0d7e2)',
          borderRadius: 8,
          background: state.cmkActiveForReads ? 'var(--success-bg, #e6f7ec)' : 'var(--surface-muted, #f5f5f7)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {state.cmkActiveForReads ? (
            <ShieldCheck size={18} aria-hidden="true" />
          ) : (
            <ShieldOff size={18} aria-hidden="true" />
          )}
          <strong>
            {state.enrolled
              ? state.state === 'disabled'
                ? 'Customer-managed key disabled — crypto-shred pending'
                : state.state === 'rotating'
                  ? 'Customer-managed key active — rotation in progress'
                  : 'Customer-managed key active'
              : 'Using platform-managed encryption keys'}
          </strong>
        </div>
        {state.enrolled && (
          <dl style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 16, rowGap: 4 }}>
            <dt>Fingerprint</dt>
            <dd>
              <code>{fingerprintShort}</code>
            </dd>
            <dt>Provider</dt>
            <dd>{state.provider}</dd>
            <dt>Persistence</dt>
            <dd>{state.persistence}</dd>
            <dt>Enrolled</dt>
            <dd>{state.enrolledAt ? new Date(state.enrolledAt).toLocaleString() : '—'}</dd>
            <dt>Last rotated</dt>
            <dd>{state.rotatedAt ? new Date(state.rotatedAt).toLocaleString() : 'Never'}</dd>
            {state.state === 'disabled' && state.disableRequestedAt && state.disableGraceDays && (
              <>
                <dt>Crypto-shred ETA</dt>
                <dd>
                  {new Date(
                    new Date(state.disableRequestedAt).getTime() + state.disableGraceDays * 24 * 60 * 60 * 1000,
                  ).toLocaleString()}
                </dd>
              </>
            )}
          </dl>
        )}
      </div>

      {/* Recovery sheet — appears for ~10min after enrol; one-time download */}
      {recoverySheet && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            border: '2px solid var(--warn-fg, #92400e)',
            borderRadius: 8,
            background: 'var(--warn-bg, #fef3c7)',
          }}
        >
          <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={16} aria-hidden="true" /> One-time recovery sheet
          </strong>
          <p className="sso-settings-hint" style={{ marginTop: 6 }}>
            Save this artefact in your secret manager NOW. It is available until{' '}
            {recoverySheetExpiresAt ? new Date(recoverySheetExpiresAt).toLocaleString() : 'soon'} and cannot be
            re-issued.
          </p>
          <button
            type="button"
            className="sso-settings-primary"
            onClick={downloadRecoverySheet}
            style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={14} aria-hidden="true" /> Download JSON
          </button>
        </div>
      )}

      {/* In-scope tables — always shown */}
      <details style={{ marginTop: 12 }}>
        <summary>What does this key protect? ({state.inScopeTables.length} columns)</summary>
        <ul style={{ marginTop: 8 }}>
          {state.inScopeTables.map((t) => (
            <li key={t}>
              <code>{t}</code>
            </li>
          ))}
        </ul>
      </details>

      {/* Prereqs (always shown) */}
      <div style={{ marginTop: 16 }}>
        <strong>Pre-requisites</strong>
        <ul>
          <li>
            {state.prerequisites.mfaMandateEnabled ? '✅' : '⛔'} Require MFA for password-auth members{' '}
            {!state.prerequisites.mfaMandateEnabled && <a href="#mfa">turn on</a>}
          </li>
          <li>
            {state.prerequisites.siemEndpointConfigured ? '✅' : '⛔'} At least one audit-log SIEM endpoint configured{' '}
            {!state.prerequisites.siemEndpointConfigured && <a href="#siem">configure</a>}
          </li>
        </ul>
      </div>

      {/* Action forms — only render when canEdit */}
      {canEdit && state.state !== 'disabled' && !state.enrolled && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleEnrol();
          }}
          style={{ marginTop: 16, display: 'grid', gap: 8 }}
        >
          <strong>Enrol a customer-managed key</strong>
          <input
            type="password"
            placeholder="base64-encoded 32-byte AES key — generate with `openssl rand -base64 32`"
            value={enrolKek}
            onChange={(e) => setEnrolKek(e.target.value)}
            disabled={busy || !prereqsOk}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <input
            type="text"
            placeholder="Optional label (e.g. vault.acme.io/keys/rynora-prod-2026q2)"
            value={enrolLabel}
            onChange={(e) => setEnrolLabel(e.target.value)}
            disabled={busy || !prereqsOk}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <input
            type="text"
            placeholder={`Type ${ENROL_CONFIRM} to confirm`}
            value={enrolConfirm}
            onChange={(e) => setEnrolConfirm(e.target.value)}
            disabled={busy || !prereqsOk}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <button
            type="submit"
            className="sso-settings-primary"
            disabled={busy || !prereqsOk || enrolConfirm !== ENROL_CONFIRM || enrolKek.trim().length < 32}
            style={{ alignSelf: 'flex-start' }}
          >
            {busy ? 'Enrolling…' : 'Enrol customer-managed key'}
          </button>
        </form>
      )}

      {canEdit && state.enrolled && state.state === 'active' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleRotate();
          }}
          style={{
            marginTop: 16,
            padding: 12,
            border: '1px solid var(--border, #d0d7e2)',
            borderRadius: 8,
            display: 'grid',
            gap: 8,
          }}
        >
          <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} aria-hidden="true" /> Rotate KEK
          </strong>
          <p className="sso-settings-hint">
            Generates a new DEK on the server, wraps it with your new KEK, and re-encrypts in-scope rows in the
            background. The previous DEK is kept for 24h to serve in-flight reads.
          </p>
          <input
            type="password"
            placeholder="New base64-encoded 32-byte AES KEK"
            value={rotateKek}
            onChange={(e) => setRotateKek(e.target.value)}
            disabled={busy}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <input
            type="text"
            placeholder="Optional new label"
            value={rotateLabel}
            onChange={(e) => setRotateLabel(e.target.value)}
            disabled={busy}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <input
            type="text"
            placeholder={`Type ${ROTATE_CONFIRM} to confirm`}
            value={rotateConfirm}
            onChange={(e) => setRotateConfirm(e.target.value)}
            disabled={busy}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <button
            type="submit"
            className="sso-settings-primary"
            disabled={busy || rotateConfirm !== ROTATE_CONFIRM || rotateKek.trim().length < 32}
            style={{ alignSelf: 'flex-start' }}
          >
            {busy ? 'Rotating…' : 'Rotate now'}
          </button>
        </form>
      )}

      {canEdit && state.enrolled && state.state !== 'disabled' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleDisable();
          }}
          style={{
            marginTop: 16,
            padding: 12,
            border: '2px solid var(--danger-fg, #b91c1c)',
            borderRadius: 8,
            display: 'grid',
            gap: 8,
            background: 'var(--danger-bg, #fef2f2)',
          }}
        >
          <strong style={{ color: 'var(--danger-fg, #b91c1c)' }}>Disable customer-managed key</strong>
          <p className="sso-settings-hint">
            Starts a 30-day grace window. After Day 30, the wrapped DEK is crypto-shredded and every in-scope row
            becomes unrecoverable by anyone (including Rynora). You can cancel the disable at any time during the grace
            window.
          </p>
          <textarea
            placeholder="Reason (required, stored for audit)"
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
            disabled={busy}
            rows={2}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <input
            type="text"
            placeholder={`Type the org slug "${orgSlug}" to confirm`}
            value={disableSlug}
            onChange={(e) => setDisableSlug(e.target.value)}
            disabled={busy}
            style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
          />
          <button
            type="submit"
            disabled={
              busy || disableReason.trim().length === 0 || disableSlug.trim().toLowerCase() !== orgSlug.toLowerCase()
            }
            style={{
              alignSelf: 'flex-start',
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid var(--danger-fg, #b91c1c)',
              color: 'var(--danger-fg, #b91c1c)',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            {busy ? 'Disabling…' : 'Start 30-day grace'}
          </button>
        </form>
      )}

      {canEdit && state.state === 'disabled' && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            border: '2px solid var(--warn-fg, #92400e)',
            borderRadius: 8,
            background: 'var(--warn-bg, #fef3c7)',
          }}
        >
          <strong>Grace window active — last chance to recover</strong>
          <p className="sso-settings-hint">
            Reason on file: <em>{state.enrolled ? '(see audit log)' : '—'}</em>
          </p>
          <button
            type="button"
            className="sso-settings-primary"
            onClick={() => void handleCancelDisable()}
            disabled={busy}
            style={{ marginTop: 8 }}
          >
            {busy ? 'Cancelling…' : 'Cancel disable & restore CMK'}
          </button>
        </div>
      )}
    </section>
  );
}
